<template>
  <div class="page-vertical-container">
    <div class="page-header">
      <div class="page-header-left">
        <img src="@/assets/images/chatnav_logo.svg" alt="ChatNav Logo" class="dashboard-logo">
        <div class="title">Admin</div>
        <nav class="main-nav">
          <router-link to="/home" class="nav-link">Servers</router-link>
          <router-link to="/users" class="nav-link">Users</router-link>
          <router-link to="/admin" class="nav-link">Admin</router-link>
        </nav>
      </div>
      <div class="page-header-right">
        <div class="user-info">
          <span class="hub-admin-badge" title="You are a ChatNav Hub Admin">Hub Admin</span>
          <span class="username">{{ currentUsername }}</span>
          <button class="logout-btn" @click="logout">Logout</button>
        </div>
      </div>
    </div>

    <div class="page-content admin-page">
      <section class="admin-section">
        <h2>Reports</h2>
        <div class="admin-action-card">
          <div class="action-info">
            <strong>OpenAI Cost Report</strong>
            <span>Generates a cost report across all OpenAI projects and downloads it as an HTML file. This may take up to a minute.</span>
          </div>
          <button class="btn-admin-action" :disabled="reportLoading" @click="generateOpenAIReport">
            <span v-if="reportLoading" class="btn-spinner"></span>
            {{ reportLoading ? 'Generating report…' : 'Generate &amp; Download' }}
          </button>
        </div>
      </section>

      <section class="admin-section">
        <h2>Deploy</h2>
        <p class="dep-intro">Pulls a git branch on each selected server and restarts the chatnav service there.</p>

        <div class="dep-panel">
          <div class="dep-step">
            <div class="dep-step-head">
              <span class="dep-step-number">1</span>
              <h3>Branch</h3>
            </div>
            <div class="dep-branch">
              <select id="deploy-branch" v-model="deployBranch" :disabled="deploying" aria-label="Branch to deploy">
                <option v-for="b in branches" :key="b" :value="b">{{ b }}</option>
              </select>
              <span v-if="branchesLoading" class="dep-hint">
                <span class="dep-spinner"></span> Loading other branches…
              </span>
              <span v-else-if="branchesError" class="dep-hint dep-hint-error">
                Other branches could not be loaded ({{ branchesError }}).
                <button class="dep-link" @click="fetchBranches">Try again</button>
              </span>
            </div>
          </div>

          <div class="dep-step">
            <div class="dep-step-head">
              <span class="dep-step-number">2</span>
              <h3>Servers</h3>
              <span class="dep-count">{{ selectedServerIds.length }} of {{ servers.length }} selected</span>
            </div>
            <div class="dep-toolbar">
              <input v-model="search" class="dep-search" type="search" placeholder="Search servers…"
                     aria-label="Search servers" :disabled="deploying">
              <button class="dep-btn" :disabled="deploying || !filteredServers.length" @click="selectVisible">
                {{ search ? 'Select shown' : 'Select all' }}
              </button>
              <button class="dep-btn" :disabled="deploying || !selectedServerIds.length" @click="clearSelection">
                Clear
              </button>
            </div>
            <div v-if="filteredServers.length" class="dep-server-grid">
              <label v-for="server in filteredServers" :key="server.server_id" class="dep-server"
                     :class="{ selected: selectedServerIds.includes(server.server_id) }">
                <input type="checkbox" v-model="selectedServerIds" :value="server.server_id" :disabled="deploying">
                <span class="dep-dot" :class="`dot-${serverState(server).tone}`" :title="serverState(server).label"></span>
                <span class="dep-server-name">{{ server.name }}</span>
                <span class="dep-server-sub">{{ server.subdomain }}</span>
              </label>
            </div>
            <p v-else class="dep-empty">{{ servers.length ? 'No server matches the search.' : 'No servers to show.' }}</p>
          </div>
        </div>

        <div class="dep-bar">
          <div class="dep-bar-info">
            <strong>{{ selectedServerIds.length }}</strong> server(s) selected
            <span class="dep-bar-branch">branch <code>{{ deployBranch }}</code></span>
          </div>
          <button class="btn-admin-action" :disabled="deploying || selectedServerIds.length === 0"
                  @click="askToConfirm">
            <span v-if="deploying" class="btn-spinner"></span>
            {{ deploying ? 'Deploying…' : `Deploy ${deployBranch} to ${selectedServerIds.length} server(s)` }}
          </button>
        </div>

        <div v-if="runServers.length" class="dep-run">
          <div class="dep-summary" aria-live="polite">
            <div class="dep-summary-text">
              <strong>{{ runCounts.online }} of {{ runCounts.total }} online</strong>
              <span v-if="runCounts.waiting">· {{ runCounts.waiting }} waiting for the service</span>
              <span v-if="runCounts.updating">· {{ runCounts.updating }} updating</span>
              <span v-if="runCounts.failed" class="dep-summary-failed">· {{ runCounts.failed }} failed</span>
            </div>
            <div class="dep-progress">
              <div class="dep-progress-ok" :style="{ width: percent(runCounts.online) }"></div>
              <div class="dep-progress-failed" :style="{ width: percent(runCounts.failed) }"></div>
            </div>
            <p v-if="healthError" class="dep-hint dep-hint-error">
              The health of the servers could not be checked ({{ healthError }}). It is tried again every few seconds.
            </p>
          </div>

          <div class="dep-console-grid">
            <div v-for="server in runServers" :key="server.server_id" class="dep-console"
                 :class="`phase-${cardOf(server).phase}`">
              <button class="dep-console-header" @click="toggleCollapsed(server.server_id)"
                      :aria-expanded="!cardOf(server).collapsed">
                <span class="dep-console-title">
                  {{ server.name }} <span class="dep-console-sub">({{ server.subdomain }})</span>
                </span>
                <span class="dep-phase" :class="`phase-${cardOf(server).phase}`">
                  <span v-if="isBusy(cardOf(server).phase)" class="dep-spinner dep-spinner-small"></span>
                  {{ phaseLabel(cardOf(server).phase) }}
                  <span v-if="elapsedOf(cardOf(server))" class="dep-elapsed">{{ elapsedOf(cardOf(server)) }}</span>
                </span>
              </button>
              <pre v-show="!cardOf(server).collapsed" class="dep-console-body"
                   :ref="'console-' + server.server_id">{{ (deployLogs[server.server_id] || []).join('\n') }}</pre>
              <div v-if="cardOf(server).error" class="dep-console-error">{{ cardOf(server).error }}</div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <div v-if="showConfirm" class="dep-overlay" @click.self="showConfirm = false" @keydown.esc="showConfirm = false">
      <div class="dep-dialog" role="dialog" aria-modal="true" aria-labelledby="dep-dialog-title">
        <h3 id="dep-dialog-title">Deploy <code>{{ deployBranch }}</code>?</h3>
        <p>
          The chatnav service will be stopped and started again on {{ selectedServerIds.length }}
          server(s). Each is unavailable until it is back up, which takes a few minutes.
        </p>
        <ul class="dep-dialog-list">
          <li v-for="server in selectedServers" :key="server.server_id">{{ server.name }}
            <span class="dep-server-sub">{{ server.subdomain }}</span></li>
        </ul>
        <div class="dep-dialog-actions">
          <button class="dep-btn" ref="cancelButton" @click="showConfirm = false">Cancel</button>
          <button class="btn-admin-action" @click="deployToServers">Deploy</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useToast } from 'vue-toastification';
import { apiFetch } from '@/services/api';
import { getAccessToken, decodeToken, clearTokens } from '@/services/auth';

const toast = useToast();

const DEFAULT_BRANCH = 'main';
const HEALTH_POLL_MS = 5000;

const PHASE_LABELS = {
  queued: 'Queued',
  updating: 'Updating',
  deployed: 'Deployed, waiting for the service',
  online: 'Online',
  failed: 'Failed',
};

// The state the Hub keeps for a server; shown as a coloured dot next to its name.
const SERVER_STATES = {
  active: { tone: 'ok', label: 'Online' },
  health_check_failed: { tone: 'bad', label: 'Not responding' },
  error: { tone: 'bad', label: 'Error' },
  creating: { tone: 'warn', label: 'Being created' },
  inactive: { tone: 'muted', label: 'Inactive' },
  trial: { tone: 'muted', label: 'Trial' },
};

export default {
  name: 'AdminPage',
  data() {
    return {
      currentUsername: '',
      reportLoading: false,
      servers: [],
      search: '',
      branches: [DEFAULT_BRANCH],
      branchesLoading: false,
      branchesError: '',
      deployBranch: DEFAULT_BRANCH,
      selectedServerIds: [],
      showConfirm: false,
      deploying: false,
      eventSource: null,
      activeRunId: null,
      deployingServers: [],
      deployLogs: {},
      cards: {},
      now: Date.now(),
      healthError: '',
      healthPolling: false,
      tickTimer: null,
      healthTimer: null,
    }
  },
  computed: {
    sortedServers() {
      return [...this.servers].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    },
    filteredServers() {
      const term = this.search.trim().toLowerCase();
      if (!term) return this.sortedServers;
      return this.sortedServers.filter((s) =>
        s.name.toLowerCase().includes(term) || s.subdomain.toLowerCase().includes(term));
    },
    selectedServers() {
      return this.sortedServers.filter((s) => this.selectedServerIds.includes(s.server_id));
    },
    // Failed servers first, as they are the ones that need a look; the rest stays alphabetical.
    runServers() {
      const failedFirst = (s) => (this.cardOf(s).phase === 'failed' ? 0 : 1);
      return [...this.deployingServers].sort((a, b) =>
        failedFirst(a) - failedFirst(b)
        || a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    },
    runCounts() {
      const counts = { total: this.deployingServers.length, online: 0, waiting: 0, updating: 0, failed: 0 };
      this.deployingServers.forEach((s) => {
        const phase = this.cardOf(s).phase;
        if (phase === 'online') counts.online += 1;
        else if (phase === 'deployed') counts.waiting += 1;
        else if (phase === 'failed') counts.failed += 1;
        else counts.updating += 1;
      });
      return counts;
    },
  },
  async mounted() {
    const payload = decodeToken(getAccessToken());
    this.currentUsername = payload ? (payload.username || payload.sub || '') : '';
    this.connectDeployLogStream();
    this.tickTimer = setInterval(() => { this.now = Date.now(); }, 1000);
    this.healthTimer = setInterval(this.pollHealth, HEALTH_POLL_MS);
    // main can be deployed at once; the other branches come from a server and arrive later.
    this.fetchBranches();
    await this.fetchServers();
  },
  beforeUnmount() {
    this.disconnectDeployLogStream();
    clearInterval(this.tickTimer);
    clearInterval(this.healthTimer);
  },
  methods: {
    logout() {
      clearTokens();
      this.$router.push('/');
    },
    serverState(server) {
      return SERVER_STATES[server.status] || { tone: 'muted', label: server.status };
    },
    cardOf(server) {
      return this.cards[server.server_id] || { phase: 'queued' };
    },
    phaseLabel(phase) {
      return PHASE_LABELS[phase];
    },
    isBusy(phase) {
      return phase === 'updating' || phase === 'deployed';
    },
    percent(count) {
      return this.runCounts.total ? `${(count / this.runCounts.total) * 100}%` : '0%';
    },
    elapsedOf(card) {
      if (!card.startedAt) return '';
      const seconds = Math.max(0, Math.floor(((card.finishedAt || this.now) - card.startedAt) / 1000));
      const minutes = Math.floor(seconds / 60);
      return minutes ? `${minutes}m ${String(seconds % 60).padStart(2, '0')}s` : `${seconds}s`;
    },
    toggleCollapsed(serverId) {
      this.cards[serverId].collapsed = !this.cards[serverId].collapsed;
    },
    selectVisible() {
      this.selectedServerIds = [...new Set([
        ...this.selectedServerIds, ...this.filteredServers.map((s) => s.server_id),
      ])];
    },
    clearSelection() {
      this.selectedServerIds = [];
    },
    connectDeployLogStream() {
      try {
        const token = getAccessToken();
        if (!token) return;
        this.disconnectDeployLogStream();
        const apiBase = process.env.VUE_APP_API_BASE_URL || '';
        const url = `${apiBase}/api/server-status-stream?token=${encodeURIComponent(token)}`;
        this.eventSource = new EventSource(url);
        this.eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'deploy_log') {
              this.handleDeployLog(data.data);
            }
          } catch (e) {
            console.error('Error parsing SSE message:', e);
          }
        };
        this.eventSource.onerror = () => {
          setTimeout(() => {
            if (this.eventSource && this.eventSource.readyState === EventSource.CLOSED) {
              this.connectDeployLogStream();
            }
          }, 5000);
        };
      } catch (e) {
        console.error('Failed to establish SSE connection:', e);
      }
    },
    disconnectDeployLogStream() {
      if (this.eventSource) {
        this.eventSource.close();
        this.eventSource = null;
      }
    },
    handleDeployLog(entry) {
      if (!entry || entry.run_id !== this.activeRunId) return;
      const serverId = entry.server_id;
      const card = this.cards[serverId];
      if (!card) return;
      if (entry.done) {
        this.finishUpdate(card, entry.success);
        return;
      }
      if (!this.deployLogs[serverId]) {
        this.deployLogs[serverId] = [];
      }
      this.deployLogs[serverId].push(entry.line);
      if (card.phase === 'queued') {
        card.phase = 'updating';
        card.startedAt = Date.now();
      }
      this.$nextTick(() => {
        const ref = this.$refs['console-' + serverId];
        const target = Array.isArray(ref) ? ref[0] : ref;
        if (target) target.scrollTop = target.scrollHeight;
      });
    },
    // The update (git and the restart of the service) is over; the server is not back up yet.
    finishUpdate(card, success) {
      if (card.phase === 'deployed' || card.phase === 'online' || card.phase === 'failed') return;
      card.startedAt = card.startedAt || Date.now();
      if (success) {
        card.phase = 'deployed';
      } else {
        card.phase = 'failed';
        card.finishedAt = Date.now();
      }
    },
    // Asks the Hub which of the deployed servers answer on /health. Nothing gives up here: a server
    // that does not come back stays "waiting" until the page is left.
    async pollHealth() {
      const waiting = this.deployingServers.filter((s) => this.cardOf(s).phase === 'deployed');
      if (!waiting.length || this.healthPolling) return;
      this.healthPolling = true;
      try {
        const response = await apiFetch('/api/servers-health', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ server_ids: waiting.map((s) => s.server_id) }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.detail || `Server error ${response.status}`);
        }
        waiting.forEach((s) => {
          const card = this.cards[s.server_id];
          if (data.healthy[s.server_id] && card.phase === 'deployed') {
            card.phase = 'online';
            card.finishedAt = Date.now();
            card.collapsed = true;
          }
        });
        this.healthError = '';
      } catch (e) {
        console.error('Health check of deployed servers failed:', e);
        this.healthError = e.message;
      } finally {
        this.healthPolling = false;
      }
    },
    async fetchServers() {
      try {
        const response = await apiFetch('/api/user-servers');
        if (response.ok) {
          this.servers = await response.json();
        }
      } catch (e) {
        toast.error('Failed to load servers.');
      }
    },
    async fetchBranches() {
      this.branchesLoading = true;
      this.branchesError = '';
      try {
        const response = await apiFetch('/api/deploy-branches');
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.detail || `Server error ${response.status}`);
        }
        const others = (data.branches || []).filter((b) => b !== DEFAULT_BRANCH).sort();
        this.branches = [DEFAULT_BRANCH, ...others];
      } catch (e) {
        this.branchesError = e.message;
      } finally {
        this.branchesLoading = false;
      }
    },
    askToConfirm() {
      this.showConfirm = true;
      this.$nextTick(() => this.$refs.cancelButton && this.$refs.cancelButton.focus());
    },
    async deployToServers() {
      this.showConfirm = false;
      const branch = this.deployBranch;
      const runId = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      this.activeRunId = runId;
      this.deployingServers = this.sortedServers.filter((s) => this.selectedServerIds.includes(s.server_id));
      this.deployLogs = {};
      this.healthError = '';
      this.cards = {};
      this.deployingServers.forEach((s) => {
        this.cards[s.server_id] = { phase: 'queued', collapsed: false, error: '' };
      });

      this.deploying = true;
      try {
        const response = await apiFetch('/api/deploy-branch', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ branch, server_ids: this.selectedServerIds, run_id: runId }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.detail || `Server error ${response.status}`);
        }
        const results = data.results || [];
        results.forEach((r) => {
          const card = this.cards[r.server_id];
          // The result also settles a server whose last log message never arrived.
          this.finishUpdate(card, r.success);
          if (!r.success) card.error = r.error || 'Deploy failed.';
        });
        const failedCount = results.filter((r) => !r.success).length;
        if (failedCount === 0) {
          toast.success(`Deployed '${branch}' to ${results.length} server(s). Waiting for them to come back.`);
        } else {
          toast.warning(`Deployed '${branch}': ${results.length - failedCount}/${results.length} succeeded, ` +
                        `${failedCount} failed.`);
        }
      } catch (e) {
        toast.error(`Deploy failed: ${e.message}`);
      } finally {
        this.deploying = false;
      }
    },
    async generateOpenAIReport() {
      this.reportLoading = true;
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 120000);
      try {
        const response = await apiFetch('/api/admin/openai-cost-report', {
          signal: controller.signal,
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.detail || `Server error ${response.status}`);
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const now = new Date();
        const dateStr = `${String(now.getDate()).padStart(2, '0')}.${String(now.getMonth() + 1).padStart(2, '0')}.${now.getFullYear()}`;
        a.href = url;
        a.download = `OpenAI Cost Report ${dateStr}.html`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (e) {
        toast.error(`Failed to generate report: ${e.message}`);
      } finally {
        clearTimeout(timeout);
        this.reportLoading = false;
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/css/home-page.scss";
@import "@/assets/css/admin.scss";
</style>
